/* eslint-disable */
import type { GeoLocation, GlobalAddress } from "../../common/location.pb";

export enum MerchantPresenceHint {
  /** BRICK_AND_MORTAR - Merchant with a physical presence */
  BRICK_AND_MORTAR = "BRICK_AND_MORTAR",
  /** INTERNET - Merchant who exist on the internet without a physical presence */
  INTERNET = "INTERNET",
  /** PHONE - Merchants who have a phone number as their locality */
  PHONE = "PHONE",
}

export interface CanonicalMerchantCategory {
  description?: string;
  canonical_category?: string;
  avatar?: string;
}

export interface Merchant {
  /** The Merchant Category Code */
  mcc?: string;
  /** The Merchant identifier provided by external system */
  mid?: string;
  /** Usually the merchant name or statement descriptor */
  description?: string;
  /** Square-curated canonicalized name for the merchant */
  canonical_name?: string;
  /** Address we received for this merchant (or null) */
  address?: GlobalAddress;
  /** Lat/Long of the merchant, if any */
  geo_location?: GeoLocation;
  /** Merchant phone number */
  phone_number?: string;
  /** Unique merchant identified generated by banklin */
  merchant_token?: string;
  /**
   * The parent merchant token under which all merchants are grouped e.g. all starbucks merchants
   * are grouped under a parent called Starbucks
   */
  parent_merchant_token?: string;
  /** Logo of the merchant */
  logo_url?: string;
  presence_hint?: MerchantPresenceHint;
  /** Sanitized version of the address */
  canonical_address?: GlobalAddress;
  /** Url (only the host part) of the merchant when presence_hint is INTERNET. */
  canonical_url?: string;
  /** Normalized version of the description e.g. numbers are removed */
  normalized_description?: string;
  /**
   * The merchant canonical category. It is pre-filled from mcc groups and sometimes over written
   * manually for certain merchants.
   */
  canonical_category?: string;
  /** Source of Merchant attributes that will be displayed to user: default = AUTO */
  attribute_source?: MerchantAttributeSource;
  /** First 'word' from description (used to match new trasactions against existing merchants) */
  description_first_word?: string;
  /** Pattern that matches the description, used for grouping merchants with similar patterns. */
  description_pattern?: string;
  /** When this is true merchants can can make unlimited unlinked refunds */
  unlimited_unlinked_refunds?: boolean;
  /** True if the merchant is blacklisted */
  blacklisted?: boolean;
  /**
   * This bit is only set for parents whose names are unique enough that any merchant whose
   * name contains the name of this parent can be grouped under it.
   * e.g. Starbucks is unique enough and this bit can be set for it but Italian Restaurant is not.
   */
  name_unique_enough_for_matching?: boolean;
  /**
   * A 6 digit hex color with leading # sign, usually derived from the merchant logo. Ex: "#BBBFB3".
   * Suitable as text color, or as solid fill (dark enough to support white icon overlays).
   */
  brand_color?: string;
  /**
   * Token of the merchant that is linked to this merchant. Use the linked merchant instead of the
   * original.
   */
  active_merchant_token?: string;
  /** True if Square has confirmed that the merchant's phone_number is accurate. */
  phone_number_confirmed?: boolean;
  /** The seller register merchant token associated with this merchant, if any. */
  register_merchant_token?: string;
  /**
   * Logo of the merchant, for use in dark mode.
   * Optional--if nil, fall back to logo_url.
   */
  logo_url_dark_theme?: string;
  /**
   * A 6 digit hex color with leading # sign, usually derived from the merchant logo. Ex: "#BBBFB3".
   * Suitable as text color, or as solid fill (dark enough to support white icon overlays).
   * For use in dark mode. Optional--if nil, fall back to brand_color.
   */
  brand_color_dark_theme?: string;
  /** Returns a normalized hash to avoid inconsistency in address formatting. */
  normalized_address_hash?: number;
  /**
   * The seller register unit token associated with this merchant, if any.
   *  One use case, for example, is Seller passing a register_unit_token to Pay With Cash
   *  when creating child Merchants. PwC then propagates this register_unit_token to Merchantein
   *  in order to try and match this child merchant to an appropriate parent merchant.
   */
  register_unit_token?: string;
  /** Returns an affiliate link agreed with merchant */
  affiliate_link_url?: string;
  /** Set to true if the canonical name was manually overridden */
  has_canonical_name_override?: boolean;
  /**
   * Set to true if the merchant is a artifact for Bizzy to maintain sync with Merchantein.
   * In general you should not operate on these merchants.
   */
  created_by_bizzy?: boolean;
  /** Square seller canonicalized merchant/unit name. */
  canonical_name_seller?: string;
  /** Returns the Afterpay Shop URL corresponding to this merchant entity. */
  ap_shop_url?: string;
  /** indicates this is a parent merchant record */
  is_parent?: boolean;
}

export enum MerchantAttributeSource {
  /** AUTO - Attributes determined from transaction information */
  AUTO = "AUTO",
  /** MANUAL - Manually entered */
  MANUAL = "MANUAL",
  /** FOURSQUARE - Found using FourSquare venue API */
  FOURSQUARE = "FOURSQUARE",
  /** INTERNAL_SERVICE - Attributes provided by an internal service */
  INTERNAL_SERVICE = "INTERNAL_SERVICE",
}
